import React, { useEffect, useState } from "react";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import {
	FrontendCompany,
	FrontendCompanyTestCategory,
	FrontendCompanyTestLabels,
	FrontendCompanyTestResultField,
	FrontendCompanyTestResultUnits,
	FrontendCompanyWithAdditionalInfomation,
	FrontendRolePermission,
	FrontendUserInfo,
} from "node-library/dist/portal";
import {
	FrontendContentPage,
	Severity,
	SystemAddOn,
	SystemRole,
} from "node-library/dist/constants";
import DesktopSidebar from "./desktopSidebar";
import UserProfilePage from "../userProfile/userProfilePage";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import UnsavedChangesModal from "../../common-components/modals/unsavedChangesModal";
import RosterPage from "../mainPages/roster/rosterPage";
import AdminViewOrdersPage from "../mainPages/viewOrders/admin_view/adminViewOrdersPage";
import { useNavigate, useParams } from "react-router-dom";
import ClientOrganizationPage from "../mainPages/clientOrganizations/clientOrganizationsPage";
import RegisterClientPortalOrderPage from "../barcodeScanner/registerClientPortalOrderPage";
import AdminViewOrderDetailsContentWrapper from "../adminViewDetailsPages/adminViewOrderDetailsContentWrapper";
import AdminViewInstancePage from "../mainPages/instancePage/adminViewInstancePage";
import AdminViewInstanceDetailsContentWrapper from "../adminViewDetailsPages/adminViewInstanceDetailsContentWrapper";
import AnalystViewInstancePage from "../mainPages/instancePage/analystView/analystViewInstancePage";
import AnalystViewInstanceDetailsContentWrapper from "../analystViewDetailsPages/analystViewInstanceDetailsContentWrapper";
import AdminViewBatchPage from "../mainPages/batchPage/adminViewBatchPage";
import BatchGenerationPage from "../mainPages/batchGenerationPage/batchGenerationPage";
import AnalystViewBatchPage from "../mainPages/batchPage/analystView/analystViewBatchPage";
import LogsPage from "../mainPages/logsPage/logsPage";
import {
	getContentPageSuffix,
	getInitialContentPageByRole,
} from "../../helpers/userHelper";
import TempFilePasswordPage from "../mainPages/tempFilePasswordPage/tempFilePasswordPage";
import WorkflowManagementBatchDetailsContentWrapper from "../workflowPage/workflowManagementBatchDetailsContentWrapper";
import BasicViewBatchDetailsContentWrapper from "../workflowPage/basicViewBatchDetailsContentWrapper";
import {
	BatchAccessLevel,
	hasPermission,
	Permissions,
} from "node-library/dist/permissions";
import LabStructurePage from "../mainPages/labStructurePage/labStructurePage";
import Config from "../../globals/Config";
import { doesCompanyHaveAddonsEnabled } from "node-library/dist/helpers/request_helper";
import LandingPageHeaderBar from "./landingPageHeaderBar";

interface Props {}

export default function LandingPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [user, setUser] = useState<FrontendUserInfo>();
	const [company, setCompany] =
		useState<FrontendCompanyWithAdditionalInfomation>();
	const [contentPage, setContentPage] = useState<FrontendContentPage>(
		FrontendContentPage.INSTANCE_PAGE,
	);

	const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(
		window.innerWidth >= 600,
	);

	//unsaved changes
	const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
		useState<boolean>(false);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {},
	);

	const [previousPage, setPreviousPage] = useState<FrontendContentPage>(
		FrontendContentPage.INSTANCE_PAGE,
	);

	const navigate = useNavigate(); // Hook to navigate programmatically
	const { page, elementId } = useParams(); // Get the "page" param from the URL

	function navigateToContentPage(page: FrontendContentPage) {
		const pageSuffix: string = getContentPageSuffix(page);
		navigate(pageSuffix);
		setContentPage(page);
	}

	function navigateToOrderDetailsPage(
		orderId: string,
		fromPage: FrontendContentPage,
	) {
		navigate(`/portal/view-order/${orderId}`);
		setContentPage(FrontendContentPage.ORDER_DETAILS_PAGE);
		setPreviousPage(fromPage);
	}

	function navigateToInstanceDetailsPage(
		instanceId: string,
		fromPage: FrontendContentPage,
	) {
		navigate(`/portal/instances/${instanceId}`);
		setContentPage(FrontendContentPage.INSTANCE_DETAILS_PAGE);
		setPreviousPage(fromPage);
	}

	function navigateToBatchDetailsPage(
		batchId: string,
		fromPage: FrontendContentPage,
	) {
		navigate(`/portal/batches/${batchId}`);
		setContentPage(FrontendContentPage.BATCH_DETAILS_PAGE);
		setPreviousPage(fromPage);
	}

	function navigateToPage(
		link: string,
		contentPage: FrontendContentPage,
		fromPage?: FrontendContentPage,
	) {
		navigate(link);
		setContentPage(contentPage);
		if (fromPage) {
			setPreviousPage(fromPage);
		}
	}
	async function signOut() {
		try {
			const response = await sendPOSTRequest("logins/signout", {});
			localStorage.removeItem("billionspectra_lims_userInfo");
			localStorage.removeItem("billionspectra_lims_companyInfo");
			localStorage.removeItem("billionspectra_lims_companyAdditionalInfo");
		} catch (err) {
			console.error(`Unable to sign out: ${err}`);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			window.location.href = "/login";
		}
	}

	// function navigateToContentPageWithUnsavedCheck(page: FrontendContentPage) {
	// 	if (contentPage === page) {
	// 		return;
	// 	}
	// 	if (unsavedChanges) {
	// 		navigateToContentPage(page);
	// 	} else {
	// 		setProceedAction(() => () => {
	// 			navigateToContentPage(page);
	// 		});
	// 		setUnsavedChangesModalOpen(true);
	// 	}
	// }

	useEffect(() => {
		// Get the user info from cookies
		const userInfo = localStorage.getItem("billionspectra_lims_userInfo");

		const companyAdditionalInfoString = localStorage.getItem(
			"billionspectra_lims_companyAdditionalInfo",
		);

		if (!userInfo || !companyAdditionalInfoString) {
			signOut();
			return;
		}
		// Load user info
		const parsedUserInfo: FrontendUserInfo = JSON.parse(userInfo);
		setUser(parsedUserInfo);

		// Load structure info
		const companyAdditionalInfo: FrontendCompanyWithAdditionalInfomation =
			JSON.parse(companyAdditionalInfoString);

		const systemRoleString = localStorage.getItem(
			"billionspectra_lims_systemRoles",
		);
		let systemRolePermissions: Array<FrontendRolePermission> | undefined =
			undefined;
		if (systemRoleString) {
			systemRolePermissions = JSON.parse(systemRoleString);
		}

		setCompany({ ...companyAdditionalInfo, systemRolePermissions });
		document.title = `LIMS – ${companyAdditionalInfo.companyName}`;
	}, []);

	useEffect(() => {
		switch (page) {
			case "new-order":
				setContentPage(FrontendContentPage.NEW_ORDER_PAGE);
				break;
			case "view-order":
				if (elementId) {
					setContentPage(FrontendContentPage.ORDER_DETAILS_PAGE);
				} else {
					setContentPage(FrontendContentPage.VIEW_ORDER_PAGE);
				}
				break;
			case "user-info":
				setContentPage(FrontendContentPage.USER_INFO_PAGE);
				break;
			case "roster":
				setContentPage(FrontendContentPage.ROSTER_PAGE);
				break;
			case "clients":
				setContentPage(FrontendContentPage.CLIENTS_PAGE);
				break;
			case "instances":
				if (elementId) {
					setContentPage(FrontendContentPage.INSTANCE_DETAILS_PAGE);
				} else {
					setContentPage(FrontendContentPage.INSTANCE_PAGE);
				}
				break;
			case "batches":
				if (elementId) {
					setContentPage(FrontendContentPage.BATCH_DETAILS_PAGE);
				} else {
					setContentPage(FrontendContentPage.BATCH_PAGE);
				}
				break;
			case "batch-generation":
				setContentPage(FrontendContentPage.BATCH_GENERATION_PAGE);
				break;
			case "logs":
				setContentPage(FrontendContentPage.LOG_PAGE);
				break;
			case "lab-structure":
				setContentPage(FrontendContentPage.LAB_STRUCTURE);
				break;
			case "file-password":
				setContentPage(FrontendContentPage.FILE_PASSWORD);
				break;

			default:
				setContentPage(FrontendContentPage.INSTANCE_PAGE); // Default page
				break;
		}
	}, [page]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (unsavedChanges) {
				const confirmationMessage = translation("UnsavedChangesMessage");
				// Standard for most browsers
				event.returnValue = confirmationMessage;
				return confirmationMessage;
			}
		};

		// Attach the event listener
		window.addEventListener("beforeunload", handleBeforeUnload);

		// Cleanup the event listener on component unmount
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [unsavedChanges]);

	// Listen for the back button or history changes
	useEffect(() => {
		const handlePopState = (event: PopStateEvent) => {
			setUnsavedChanges(false);
		};

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, []);

	let contentPageElement: React.ReactNode;
	switch (contentPage) {
		case FrontendContentPage.NEW_ORDER_PAGE:
			if (
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.orders.create)
			) {
				contentPageElement = (
					<RegisterClientPortalOrderPage
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						user={user}
						setUnsavedChanges={setUnsavedChanges}
						gotoViewOrderPage={() => {
							navigateToContentPage(FrontendContentPage.VIEW_ORDER_PAGE);
						}}
					></RegisterClientPortalOrderPage>
				);
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.VIEW_ORDER_PAGE:
			if (
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.orders.list)
			) {
				contentPageElement = (
					<AdminViewOrdersPage
						user={user}
						company={company as FrontendCompanyWithAdditionalInfomation}
						setUnsavedChanges={setUnsavedChanges}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
					></AdminViewOrdersPage>
				);
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.USER_INFO_PAGE:
			contentPageElement = (
				<UserProfilePage
					user={user}
					setUser={setUser}
					setUnsavedChanges={setUnsavedChanges}
				></UserProfilePage>
			);

			break;
		case FrontendContentPage.LOG_PAGE:
			if (
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.logs.search)
			) {
				contentPageElement = <LogsPage></LogsPage>;
			}

			break;
		case FrontendContentPage.LAB_STRUCTURE:
			if (user?.role && user.role.roleKey === SystemRole.SYSTEM_ADMIN) {
				contentPageElement = (
					<LabStructurePage
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						user={user}
					></LabStructurePage>
				);
			}

			break;
		case FrontendContentPage.FILE_PASSWORD:
			if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.tempPassword.generate,
				)
			) {
				contentPageElement = (
					<TempFilePasswordPage
						user={user}
						company={company as FrontendCompanyWithAdditionalInfomation}
						setUnsavedChanges={setUnsavedChanges}
					></TempFilePasswordPage>
				);
			}

			break;
		case FrontendContentPage.INSTANCE_PAGE:
			if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.sampleInstances.adminList,
				)
			) {
				contentPageElement = (
					<AdminViewInstancePage
						company={company as FrontendCompanyWithAdditionalInfomation}
						user={user}
						navigateToInstanceDetailsPage={navigateToInstanceDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
					></AdminViewInstancePage>
				);
			} else if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.sampleInstances.analystList,
				)
			) {
				contentPageElement = (
					<AnalystViewInstancePage
						company={company as FrontendCompanyWithAdditionalInfomation}
						user={user}
						navigateToInstanceDetailsPage={navigateToInstanceDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
					></AnalystViewInstancePage>
				); ///TODO
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.ROSTER_PAGE:
			if (
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.users.list)
			) {
				contentPageElement = (
					<RosterPage
						company={company as FrontendCompanyWithAdditionalInfomation}
						user={user}
						setUnsavedChanges={setUnsavedChanges}
					></RosterPage>
				);
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.CLIENTS_PAGE:
			if (
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.clientOrgs.list)
			) {
				contentPageElement = (
					<ClientOrganizationPage
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						user={user}
						setUnsavedChanges={setUnsavedChanges}
						gotoViewOrderPage={() => {
							navigateToContentPage(FrontendContentPage.VIEW_ORDER_PAGE);
						}}
					></ClientOrganizationPage>
				);
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.ORDER_DETAILS_PAGE:
			if (
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.orders.get)
			) {
				contentPageElement = (
					<AdminViewOrderDetailsContentWrapper
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						orderId={elementId as string}
						navigateToPreviousPage={() => {
							navigateToContentPage(previousPage);
						}}
						goToViewOrderPage={() => {
							navigateToContentPage(FrontendContentPage.VIEW_ORDER_PAGE);
						}}
						user={user}
						setUnsavedChanges={setUnsavedChanges}
					/>
				);
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.INSTANCE_DETAILS_PAGE:
			if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.sampleInstances.adminGet,
				)
			) {
				contentPageElement = (
					<AdminViewInstanceDetailsContentWrapper
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						instanceId={elementId as string}
						user={user}
						goBackToPreviousPage={() => {
							navigateToContentPage(previousPage);
						}}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
					/>
				);
			} else if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.sampleInstances.analystGet,
				)
			) {
				contentPageElement = (
					<AnalystViewInstanceDetailsContentWrapper
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						instanceId={elementId as string}
						user={user}
						goBackToPreviousPage={() => {
							navigateToContentPage(previousPage);
						}}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
					></AnalystViewInstanceDetailsContentWrapper>
				);
			}
			break;
		case FrontendContentPage.BATCH_PAGE:
			if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.batches.listOverview,
				) &&
				user?.role.accessLevel.limitedBatchAccess ===
					BatchAccessLevel.FULL_ACCESS
			) {
				contentPageElement = (
					<AdminViewBatchPage
						company={company as FrontendCompanyWithAdditionalInfomation}
						user={user}
						navigateToBatchDetailsPage={navigateToBatchDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
					></AdminViewBatchPage>
				);
			} else if (
				user?.role &&
				hasPermission(
					user?.role?.permissions,
					Permissions.batches.listOverview,
				) &&
				[
					BatchAccessLevel.LIMITED_ACCESS,
					BatchAccessLevel.ASSIGNED_ONLY,
				].includes(user?.role.accessLevel.limitedBatchAccess)
			) {
				contentPageElement = (
					<AnalystViewBatchPage
						company={company as FrontendCompanyWithAdditionalInfomation}
						user={user}
						navigateToBatchDetailsPage={navigateToBatchDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
					></AnalystViewBatchPage>
				); ///TODO
			} else {
				contentPageElement = <></>;
			}
			break;
		case FrontendContentPage.BATCH_DETAILS_PAGE:
			if (
				company &&
				doesCompanyHaveAddonsEnabled(company, [SystemAddOn.WORKFLOW_MANAGEMENT])
			) {
				contentPageElement = (
					<WorkflowManagementBatchDetailsContentWrapper
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						batchId={elementId as string}
						user={user}
						goBackToPreviousPage={() => {
							navigateToContentPage(previousPage);
						}}
						navigateToOrderDetailsPage={navigateToOrderDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
					/>
				);
			} else {
				contentPageElement = (
					<BasicViewBatchDetailsContentWrapper
						batchId={elementId as string}
						user={user}
						goBackToPreviousPage={() => {
							navigateToContentPage(previousPage);
						}}
						setUnsavedChanges={setUnsavedChanges}
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
					></BasicViewBatchDetailsContentWrapper>
				);
			}
			break;
		case FrontendContentPage.BATCH_GENERATION_PAGE:
			if (
				user?.role &&
				(hasPermission(
					user?.role?.permissions,
					Permissions.batches.adminGenerate,
				) ||
					hasPermission(
						user?.role?.permissions,
						Permissions.batches.analystGenerate,
					))
			) {
				contentPageElement = (
					<BatchGenerationPage
						user={user}
						company={company as FrontendCompanyWithAdditionalInfomation}
						setCompany={setCompany}
						navigateToBatchDetailsPage={navigateToBatchDetailsPage}
						setUnsavedChanges={setUnsavedChanges}
					></BatchGenerationPage>
				);
			} else {
				contentPageElement = <></>;
			}
	}

	return !user ? (
		<h2>Loading...</h2>
	) : (
		<div>
			<LandingPageHeaderBar
				company={company as FrontendCompanyWithAdditionalInfomation}
				user={user}
				contentPage={contentPage}
				navigateToPage={navigateToPage}
				signOut={signOut}
			/>
			<div className="landing-page-container">
				<DesktopSidebar
					company={company as FrontendCompanyWithAdditionalInfomation}
					user={user}
					contentPage={contentPage}
					unsavedChanges={unsavedChanges}
					navigateToContentPage={navigateToContentPage}
					isExpanded={isSidebarExpanded}
					setIsExpanded={setIsSidebarExpanded}
					setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
					setProceedAction={setProceedAction}
					signOut={signOut}
				></DesktopSidebar>
				<div
					className={`landing-page-content-container ${
						isSidebarExpanded ? `` : `content-container-sidebar-collapsed`
					}`}
				>
					{contentPageElement}
				</div>
				<div className="version-footer">{`Version ${Config.appVersion}`}</div>
			</div>
			<UnsavedChangesModal
				open={unsavedChangesModalOpen}
				setOpen={setUnsavedChangesModalOpen}
				setUnsavedChanges={setUnsavedChanges}
				proceedAction={proceedAction}
			></UnsavedChangesModal>
		</div>
	);
}
